import React from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Avatar,
  List,
  Tag,
  Button,
  Table,
  Typography,
  Space,
  ConfigProvider
} from 'antd';
import {
  UserOutlined,
  MessageOutlined,
  ReadOutlined,
  GlobalOutlined,
  CalendarOutlined,
  SolutionOutlined,
  FormOutlined,
  CameraOutlined,
  CarOutlined,
  TeamOutlined,
  ArrowUpOutlined,
  BellOutlined
} from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;

// --- Theme Configuration ---
const THEME = {
  primary: '#3C007A',
  accent: '#FF8F00',
  bgLight: '#f0f2f5',
  fontFamily: "'Rubik', sans-serif",
};

// --- Mock Data ---
const jobPostings = [
  { key: '1', title: 'Senior Steel Engineer', candidates: 12, status: 'Active' },
  { key: '2', title: 'HR Manager', candidates: 5, status: 'Active' },
  { key: '3', title: 'Logistics Coordinator', candidates: 28, status: 'Closed' },
];

const eventData = [
  { title: 'Annual Steel Summit', date: '25 Dec 2025', tickets: 150 },
  { title: 'Safety Workshop', date: '10 Jan 2026', tickets: 45 },
];

const lmsCourses = [
  { title: 'Industrial Safety 101', progress: 85 },
  { title: 'Advanced Metallurgy', progress: 40 },
];

// --- Component ---
const EnterpriseDashboard = () => {
  
  // Custom Card Component for Consistency
  const DashboardCard = ({ title, icon, children, extra }) => (
    <Card
      title={
        <Space>
          <div style={{ color: THEME.primary, fontSize: 20 }}>{icon}</div>
          <Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 18, color: THEME.primary }}>{title}</Text>
        </Space>
      }
      extra={extra}
      bordered={false}
      style={{
        height: '100%',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        fontFamily: THEME.fontFamily
      }}
      headStyle={{ borderBottom: '1px solid #f0f0f0' }}
    >
      {children}
    </Card>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: THEME.primary,
          fontFamily: THEME.fontFamily,
          fontSize: 18,
        },
      }}
    >
      <Layout style={{ minHeight: '100vh', fontFamily: THEME.fontFamily }}>
        <Layout style={{ background: THEME.bgLight }}>
          {/* Main Content */}
          <Content style={{ margin: '24px', minHeight: 280 }}>
            
            {/* Top Section: Profile Health & Quick Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} md={16} lg={18}>
                <Card bordered={false} style={{ borderRadius: 8, background: `linear-gradient(90deg, ${THEME.primary} 0%, #5e35b1 100%)`, color: '#fff' }}>
                  <Row align="middle" gutter={24}>
                    <Col flex="100px">
                      <Progress type="circle" percent={85} strokeColor={THEME.accent} trailColor="rgba(255,255,255,0.2)" format={percent => <span style={{color:'#fff', fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 20}}>{percent}%</span>} />
                    </Col>
                    <Col flex="auto">
                      <Title level={3} style={{ color: '#fff', margin: 0, fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 24 }}>Company Profile Completeness</Title>
                      <Text style={{ color: 'rgba(255,255,255,0.8)', fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>Great job! Your profile is nearly complete. Add your latest GST certificate to reach 100%.</Text>
                      <div style={{ marginTop: 12 }}>
                        <Button size="middle" style={{ color: THEME.primary, borderColor: '#fff', fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>Update Profile</Button>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Card bordered={false} style={{ height: '100%', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Statistic 
                    title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16, color: '#666' }}>Active Support Queries</Text>}
                    value={3} 
                    valueStyle={{ color: THEME.primary, fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 32 }} 
                    prefix={<MessageOutlined style={{ fontSize: 24 }} />} 
                    suffix={<Tag color="orange" style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Pending</Tag>}
                  />
                </Card>
              </Col>
            </Row>

            {/* Module Grid */}
            <Row gutter={[16, 16]}>
              
              {/* Column 1: Operations & HR */}
              <Col xs={24} lg={8}>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  
                  {/* Job Posting Module */}
                  <DashboardCard 
                    title="Job Postings" 
                    icon={<SolutionOutlined />} 
                    extra={<a href="#" style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16, color: THEME.accent }}>View All</a>}
                  >
                    <Table 
                      dataSource={jobPostings} 
                      pagination={false} 
                      size="small"
                      columns={[
                        { 
                          title: <Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16, color: THEME.primary }}>Role</Text>, 
                          dataIndex: 'title', 
                          key: 'title',
                          render: (text) => <Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 15 }}>{text}</Text>
                        },
                        { 
                          title: <Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16, color: THEME.primary }}>Apps</Text>, 
                          dataIndex: 'candidates', 
                          key: 'candidates', 
                          render: (c) => <Tag color="blue" style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>{c}</Tag> 
                        },
                      ]}
                    />
                    <Button type="dashed" block style={{ marginTop: 12, borderColor: THEME.accent, color: THEME.accent, fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>+ Post New Job</Button>
                  </DashboardCard>

                  {/* LMS Module */}
                  <DashboardCard title="LMS Training" icon={<ReadOutlined />}>
                    <List
                      itemLayout="horizontal"
                      dataSource={lmsCourses}
                      renderItem={item => (
                        <List.Item>
                          <List.Item.Meta
                            title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>{item.title}</Text>}
                            description={<Progress percent={item.progress} size="small" strokeColor={THEME.primary} />}
                          />
                        </List.Item>
                      )}
                    />
                  </DashboardCard>

                  {/* Rides */}
                  <DashboardCard title="Corporate Rides" icon={<CarOutlined />}>
                    <Row gutter={16} style={{ textAlign: 'center' }}>
                      <Col span={12}>
                        <Statistic 
                          title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Active Rides</Text>}
                          value={12} 
                          valueStyle={{ color: '#3f8600', fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 24 }} 
                          prefix={<ArrowUpOutlined />} 
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic 
                          title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Scheduled</Text>}
                          value={5}
                          valueStyle={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 24 }}
                        />
                      </Col>
                    </Row>
                    <div style={{marginTop: 10}}>
                      <Text type="secondary" style={{fontSize: 14, fontFamily: THEME.fontFamily, fontWeight: 400}}>Next ride: MH-31-CB-1234 (20 mins)</Text>
                    </div>
                  </DashboardCard>
                </Space>
              </Col>

              {/* Column 2: Events & Social */}
              <Col xs={24} lg={8}>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  
                  {/* Events */}
                  <DashboardCard title="Upcoming Events" icon={<CalendarOutlined />}>
                    <List
                      itemLayout="horizontal"
                      dataSource={eventData}
                      renderItem={item => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={<Avatar shape="square" size="large" style={{ backgroundColor: THEME.accent, fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>{item.date.split(' ')[0]}</Avatar>}
                            title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>{item.title}</Text>}
                            description={
                              <Space>
                                <Tag style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>{item.date}</Tag>
                                <Text type="secondary" style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>{item.tickets} sold</Text>
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </DashboardCard>

                  {/* Social & Expo */}
                  <DashboardCard title="Social & Expo" icon={<GlobalOutlined />}>
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Card type="inner" title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16, color: THEME.primary }}>Blogs</Text>} size="small">
                          <Statistic value={12} suffix={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Posted</Text>} valueStyle={{ fontSize: 20, fontFamily: THEME.fontFamily, fontWeight: 400 }} />
                        </Card>
                      </Col>
                      <Col span={12}>
                        <Card type="inner" title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16, color: THEME.primary }}>Expo Stalls</Text>} size="small">
                          <Statistic value={2} suffix={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Booked</Text>} valueStyle={{ fontSize: 20, fontFamily: THEME.fontFamily, fontWeight: 400 }} />
                        </Card>
                      </Col>
                    </Row>
                  </DashboardCard>

                  {/* Alekh (Forms) */}
                  <DashboardCard title="Alekh Forms" icon={<FormOutlined />}>
                    <Space direction="vertical" style={{width: '100%'}}>
                      <div style={{ display: 'flex', justifyContent: 'space-between'}}>
                        <Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>Vendor Registration</Text>
                        <Tag color="green" style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Active</Tag>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between'}}>
                        <Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>Employee Survey</Text>
                        <Tag color="default" style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Draft</Tag>
                      </div>
                    </Space>
                  </DashboardCard>
                </Space>
              </Col>

              {/* Column 3: Tech & Security */}
              <Col xs={24} lg={8}>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  
                  {/* CRM */}
                  <DashboardCard title="CRM Pipeline" icon={<TeamOutlined />}>
                    <Row gutter={8} style={{ marginBottom: 16 }}>
                      <Col span={8}>
                        <Statistic 
                          title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Leads</Text>}
                          value={145} 
                          valueStyle={{ fontSize: 18, fontFamily: THEME.fontFamily, fontWeight: 400 }} 
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic 
                          title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Deals</Text>}
                          value={28} 
                          valueStyle={{ fontSize: 18, color: THEME.accent, fontFamily: THEME.fontFamily, fontWeight: 400 }} 
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic 
                          title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Revenue</Text>}
                          value="1.2M" 
                          valueStyle={{ fontSize: 18, fontFamily: THEME.fontFamily, fontWeight: 400 }} 
                        />
                      </Col>
                    </Row>
                    <Progress percent={60} strokeColor={THEME.primary} showInfo={false} />
                    <Text type="secondary" style={{ fontSize: 14, fontFamily: THEME.fontFamily, fontWeight: 400 }}>Monthly Goal Progress</Text>
                  </DashboardCard>

                  {/* CCTV */}
                  <DashboardCard title="Smart Surveillance (CCTV)" icon={<CameraOutlined />}>
                    <div style={{ background: '#000', height: '100px', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                      <Text style={{ color: 'lime', fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>● Live Feed Active</Text>
                    </div>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Statistic 
                          title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Faces Detected</Text>}
                          value={432} 
                          valueStyle={{ fontSize: 18, fontFamily: THEME.fontFamily, fontWeight: 400 }} 
                          prefix={<UserOutlined />} 
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic 
                          title={<Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>Alerts Today</Text>}
                          value={2} 
                          valueStyle={{ fontSize: 18, color: 'red', fontFamily: THEME.fontFamily, fontWeight: 400 }} 
                          prefix={<BellOutlined />} 
                        />
                      </Col>
                    </Row>
                  </DashboardCard>
                  
                  {/* Contact AID */}
                  <DashboardCard title="Contact AID Status" icon={<MessageOutlined />}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
                      <Text style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16 }}>Ticket #2024-88</Text>
                      <Tag color="processing" style={{ fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 14 }}>In Progress</Tag>
                    </div>
                    <Text type="secondary" style={{ fontSize: 14, fontFamily: THEME.fontFamily, fontWeight: 400 }}>Regarding: Membership Renewal</Text>
                    <Button type="link" size="small" style={{ padding: 0, fontFamily: THEME.fontFamily, fontWeight: 400, fontSize: 16, color: THEME.accent }}>View Thread</Button>
                  </DashboardCard>

                </Space>
              </Col>

            </Row>
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export default EnterpriseDashboard;



